import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api, getDeviceId } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PinDots, PinKeypad } from '@/components/PinKeypad';

/**
 * Phase 17 — PIN Setup screen.
 *
 * Shown right after a successful OTP login on any device that does not
 * yet have a PIN bound. The user can either set a PIN (skip OTP next
 * time on this device) or skip and continue with OTP-only auth.
 *
 * Flow: enter PIN → confirm PIN → POST /auth/pin/set → home.
 */
export default function PinSetup() {
    const { completePinGate } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [step, setStep] = useState('enter');     // 'enter' | 'confirm'
    const [pin1, setPin1] = useState('');
    const [pin2, setPin2] = useState('');
    const [busy, setBusy] = useState(false);
    const deviceIdRef = useRef(null);

    useEffect(() => { (async () => { deviceIdRef.current = await getDeviceId(); })(); }, []);

    const onKey = (k) => {
        if (busy) return;
        if (step === 'enter') {
            if (k === 'del') return setPin1((p) => p.slice(0, -1));
            if (pin1.length >= 4) return;
            const next = pin1 + k;
            setPin1(next);
            if (next.length === 4) setTimeout(() => setStep('confirm'), 150);
        } else {
            if (k === 'del') return setPin2((p) => p.slice(0, -1));
            if (pin2.length >= 4) return;
            const next = pin2 + k;
            setPin2(next);
            if (next.length === 4) setTimeout(() => submit(next), 150);
        }
    };

    const submit = async (confirm) => {
        if (pin1 !== confirm) {
            toast.error("PINs don't match. Try again.");
            setPin1(''); setPin2(''); setStep('enter');
            return;
        }
        if (!deviceIdRef.current) {
            deviceIdRef.current = await getDeviceId();
        }
        setBusy(true);
        try {
            await api.pinSet(deviceIdRef.current, pin1);
            toast.success('PIN set — next time you open Nia just enter your 4 digits.');
            completePinGate();
            const next = loc.state?.from || '/';
            nav(next, { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not set PIN');
            setPin1(''); setPin2(''); setStep('enter');
        } finally { setBusy(false); }
    };

    const skip = () => {
        completePinGate();
        const next = loc.state?.from || '/';
        nav(next, { replace: true });
    };

    const back = () => {
        if (step === 'confirm') { setPin2(''); setStep('enter'); return; }
        setPin1('');
    };

    return (
        <div className="nia-page" data-testid="pin-setup">
            <div className="mobile-shell" style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
                <header style={{ padding: '22px 18px 8px' }}>
                    <button onClick={skip} className="nia-btn-secondary"
                        style={{ float: 'right', padding: '6px 12px', fontSize: 12 }}
                        data-testid="pin-setup-skip">Skip for now</button>
                    <div style={{ color: 'var(--nia-subtext)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', clear: 'both', paddingTop: 8 }}>Quick unlock</div>
                    <h1 className="font-display" style={{ fontSize: 26, fontWeight: 900, margin: '2px 0 0' }}>
                        {step === 'enter' ? 'Create a 4-digit PIN' : 'Confirm your PIN'}
                    </h1>
                    <div style={{ color: 'var(--nia-subtext)', fontSize: 13, marginTop: 8, lineHeight: 1.55 }}>
                        {step === 'enter'
                            ? 'Skip the email or SMS code next time you open Nia on this device.'
                            : 'Re-enter the same 4 digits to confirm.'}
                    </div>
                </header>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 18, gap: 24 }}>
                    <PinDots length={4} filled={step === 'enter' ? pin1.length : pin2.length} testid={`pin-setup-dots-${step}`} />
                    <PinKeypad onKey={onKey} disabled={busy} testid="pin-setup-keypad" />
                </div>

                <footer style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {step === 'confirm' && (
                        <button onClick={back} className="nia-btn-secondary"
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                            data-testid="pin-setup-back">
                            <ArrowLeft size={14} /> Use a different PIN
                        </button>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--nia-subtext)', lineHeight: 1.55, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <ShieldCheck size={14} color="var(--nia-primary)" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>Your PIN unlocks this device only. If you forget it or get a new phone, sign in again with email or phone.</span>
                    </div>
                </footer>
            </div>
        </div>
    );
}
